import { useEffect, useRef, useState, type ReactNode } from 'react';

interface CloseWatcherLike {
  onclose: (() => void) | null;
  destroy(): void;
}

/**
 * A button that toggles a floating panel. Closes on outside click, Escape,
 * or when the panel asks (e.g. after following a link).
 */
export function Popover({
  trigger,
  label,
  children,
  align = 'right',
  className = '',
}: {
  trigger: ReactNode;
  label: string;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    // Android: the back button or gesture closes the menu instead of leaving
    // the page (Chrome 120+; other browsers just ignore this).
    const Watcher = (window as { CloseWatcher?: new () => CloseWatcherLike }).CloseWatcher;
    const watcher = Watcher ? new Watcher() : null;
    if (watcher) watcher.onclose = () => setOpen(false);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      watcher?.destroy();
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center rounded-lg p-2 text-muted transition-colors hover:bg-surface-2 hover:text-text"
      >
        {trigger}
      </button>
      {open && (
        <div
          className={`absolute top-full z-40 mt-2 rounded-xl border border-line bg-surface shadow-xl ${
            align === 'right' ? 'right-0' : 'left-0'
          } ${className}`}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
